import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Coffee, Heart, Loader2, Info } from 'lucide-react';
import api from '@/utils/api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export const ContributePage = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('5.00');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const suggestedAmounts = [2, 5, 10, 20];

  const handleContribute = async () => {
    const numAmount = parseFloat(amount);

    if (isNaN(numAmount) || numAmount < 0.5 || numAmount > 100) {
      setError('El monto debe estar entre €0.50 y €100.00');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(
        '/payments/create-tip',
        null,
        { params: { amount: numAmount } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Error creating tip checkout:', err);
      setError('Error al procesar la contribución. Inténtalo de nuevo.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-full bg-[#8FBC8F]/20">
                <Coffee className="w-12 h-12 text-[#8FBC8F]" />
              </div>
            </div>
            <CardTitle className="text-3xl mb-2">Contribuir</CardTitle>
            <CardDescription className="text-base">
              Apoya el desarrollo de esta app
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Tu contribución nos ayuda a mantener la app.</p>
                  <p>Es totalmente voluntaria y no es un pago obligatorio. Cualquier aporte, por pequeño que sea, nos ayuda a seguir mejorando para ti.</p>
                </div>
              </div>
            </div>

            {/* Suggested amounts */}
            <div>
              <label className="block text-sm font-medium mb-3">
                Importes sugeridos
              </label>
              <div className="grid grid-cols-4 gap-3">
                {suggestedAmounts.map((amt) => (
                  <Button
                    key={amt}
                    variant={amount === amt.toFixed(2) ? 'default' : 'outline'}
                    className={amount === amt.toFixed(2) ? 'bg-[#8FBC8F] hover:bg-[#7aad7a]' : ''}
                    onClick={() => {
                      setAmount(amt.toFixed(2));
                      setError('');
                    }}
                  >
                    €{amt}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom amount */}
            <div>
              <label className="block text-sm font-medium mb-2">
                O elige tu propio importe
              </label>
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                    <Input
                      type="number"
                      step="0.50"
                      min="0.50"
                      max="100.00"
                      value={amount}
                      onChange={(e) => {
                        setAmount(e.target.value);
                        setError('');
                      }}
                      className="pl-8"
                      placeholder="5.00"
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-red-500 mt-1">{error}</p>
                  )}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="icon">
                        <Info className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-sm">
                        Tu contribución nos ayuda a mantener la app.<br />
                        Es totalmente voluntaria.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Mínimo: €0.50 | Máximo: €100.00
              </p>
            </div>

            {/* Submit button */}
            <Button
              className="w-full bg-[#8FBC8F] hover:bg-[#7aad7a] text-lg h-12"
              onClick={handleContribute}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Heart className="w-5 h-5 mr-2" />
                  Contribuir €{parseFloat(amount || 0).toFixed(2)}
                </>
              )}
            </Button>

            {/* Thank you message */}
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-gray-600">
                ¡Gracias por tu apoyo! 💚
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Tu contribución nos motiva a seguir mejorando cada día.
              </p>
            </div>

            {/* Back button */}
            <div className="text-center">
              <Button variant="outline" onClick={() => navigate('/')}>
                Volver al inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};